import { deepClone } from '@src/utility/deepClone';
import { readRippleOverlaps } from '@src/utility/extraElementData';
import { toJS } from 'mobx';

import type { Element } from './Element';
import type { CompositionElement } from './elements/CompositionElement';
import type { ImageElement } from './elements/ImageElement';
import type {
  ExtraElementData as ExtraElementDataType,
  Video,
} from '@src/types.ts/story';

export class ExtraElementData {
  // will be modified
  private _data: Video['extraElementData'];

  constructor(data: Video['extraElementData']) {
    this._data = data;
  }

  /**
   * Removes data for an element from extraElementData
   * @param elementId ID of the element to remove data for
   */
  removeElementData(elementId: string): void {
    if (this._data[elementId]) {
      delete this._data[elementId];
    }
  }

  getElementData(element: Element): ExtraElementDataType | undefined {
    return this._data[element.id] as ExtraElementDataType;
  }

  get value(): Video['extraElementData'] {
    return this._data;
  }

  cloneValue(): Video['extraElementData'] {
    return deepClone(toJS(this._data));
  }

  restoreValue(snapshot: Video['extraElementData']): void {
    for (const key of Object.keys(this._data)) {
      delete this._data[key];
    }
    for (const [key, value] of Object.entries(snapshot)) {
      this._data[key] = value;
    }
  }

  /**
   * The overlaps this element was placed with, for reversing the ripple its add
   * applied. `addElement` gets them from the template because the element does
   * not exist yet; a removal reads them from here instead, so no caller has to
   * remember to supply them (PLAT-11535).
   */
  getRippleOverlaps(element: Element): {
    overlapBefore: number;
    overlapAfter: number;
  } {
    return readRippleOverlaps(this._data[element.id]);
  }

  isMarkedAsLogo(element: ImageElement): boolean {
    return !!(this._data[`logo_el_${element.id}`] as ExtraElementDataType)
      ?.isLogo;
  }

  isDesignComposition(element: CompositionElement): boolean {
    return !!(this._data[element.id] as ExtraElementDataType)
      ?.isDesignComposition;
  }

  isCompositionFullscreen(element: CompositionElement): boolean {
    return !!(this._data[element.id] as ExtraElementDataType)?.isFullscreen;
  }

  getCompositionMetadata(element: CompositionElement) {
    const data = this._data[element.id] as Record<string, unknown>;
    return {
      isDesignComposition: data?.isDesignComposition,
      sixteenByNine: data?.sixteenByNine,
      nineBySixteen: data?.nineBySixteen,
      oneByOne: data?.oneByOne,
    };
  }

  /**
   * Copies extraElementData from one element ID to another (deep clone).
   * Optionally re-keys logo_el_ prefixed entries for inner composition elements.
   *
   * @param sourceElementId  Original element ID
   * @param targetElementId  New element ID to copy data to
   * @param innerIdMapping   Optional map of { oldInnerId -> newInnerId } for
   *                         re-keying inner element entries (e.g., logo_el_ keys)
   */
  copyElementData(
    sourceElementId: string,
    targetElementId: string,
    innerIdMapping?: Record<string, string>,
  ): void {
    const sourceData = this._data[sourceElementId];
    if (sourceData) {
      this._data[targetElementId] = deepClone(sourceData);
    }

    if (innerIdMapping) {
      for (const [oldInnerId, newInnerId] of Object.entries(innerIdMapping)) {
        const logoData = this._data[`logo_el_${oldInnerId}`];
        if (logoData) {
          this._data[`logo_el_${newInnerId}`] = deepClone(logoData);
        }
      }
    }
  }

  /**
   * Propagates extraElementData from a source element to both halves of a split.
   * Handles:
   * - Primary metadata (keyed by element ID) via copyElementData
   * - Inner composition element ID re-keying (old inner IDs -> new inner IDs)
   * - Top-level logo_el_ entries re-keyed from source element ID to both new IDs
   *
   * @returns Object with arrays of created keys for undo tracking:
   *   - copiedElementIds: target element IDs that received primary metadata
   *   - copiedLogoKeys: logo_el_ keys created for both halves (top-level + inner)
   */
  propagateOnSplit(
    sourceElement: Element,
    firstElement: Element,
    secondElement: Element,
  ): { copiedElementIds: string[]; copiedLogoKeys: string[] } {
    const buildInnerIdMapping = (
      newElement: Element,
    ): Record<string, string> => {
      const mapping: Record<string, string> = {};
      if (
        sourceElement.type === 'composition' &&
        newElement.type === 'composition'
      ) {
        const originalElements = (sourceElement as CompositionElement).elements;
        const newElements = (newElement as CompositionElement).elements;
        for (
          let i = 0;
          i < originalElements.length && i < newElements.length;
          i++
        ) {
          if (originalElements[i].id !== newElements[i].id) {
            mapping[originalElements[i].id] = newElements[i].id;
          }
        }
      }
      return mapping;
    };

    const firstInnerMapping = buildInnerIdMapping(firstElement);
    const secondInnerMapping = buildInnerIdMapping(secondElement);

    this.copyElementData(sourceElement.id, firstElement.id, firstInnerMapping);

    this.copyElementData(
      sourceElement.id,
      secondElement.id,
      secondInnerMapping,
    );

    // Copy the top-level logo_el_ entry for the element itself
    const sourceLogoKey = `logo_el_${sourceElement.id}`;
    const sourceLogoData = this._data[sourceLogoKey];
    if (sourceLogoData) {
      this._data[`logo_el_${firstElement.id}`] = deepClone(sourceLogoData);
      this._data[`logo_el_${secondElement.id}`] = deepClone(sourceLogoData);
    }

    // Build undo-tracking arrays
    const copiedElementIds = [firstElement.id, secondElement.id];
    const copiedLogoKeys = [
      `logo_el_${firstElement.id}`,
      `logo_el_${secondElement.id}`,
      ...Object.values(firstInnerMapping).map(id => `logo_el_${id}`),
      ...Object.values(secondInnerMapping).map(id => `logo_el_${id}`),
    ];

    return { copiedElementIds, copiedLogoKeys };
  }

  /**
   * Reconciles video_template_settings after crop/removal operations.
   * Removes entries for elements no longer on the timeline and updates
   * IDs for elements that were split.
   *
   * Also cleans up per-element metadata keys for entries that were dropped.
   *
   * @param keptElementIds  Set of element IDs that survived the crop
   * @param idMapping       Map of originalId -> newId for split elements
   */
  reconcileVideoTemplateSettings(
    keptElementIds: Set<string>,
    idMapping: Map<string, string>,
  ): void {
    const settingsKey = 'video_template_settings';
    const settings = this._data[settingsKey] as
      | {
          videoTemplateElements?: Array<{ id: string; [key: string]: unknown }>;
          [key: string]: unknown;
        }
      | undefined;

    if (!settings || !Array.isArray(settings.videoTemplateElements)) {
      return;
    }

    const originalEntries = settings.videoTemplateElements;
    const reconciledEntries: Array<{ id: string; [key: string]: unknown }> = [];
    const removedIds: string[] = [];

    for (const entry of originalEntries) {
      // Captions entries use a karaoke template/config ID, not a timeline
      // element ID.  Their lifecycle is managed by karaoke-track reconciliation,
      // so we always preserve them here.
      if (entry.type === 'captions') {
        reconciledEntries.push(entry);
        continue;
      }

      const newId = idMapping.get(entry.id);
      if (newId && keptElementIds.has(newId)) {
        // Element was split — update to the new ID
        reconciledEntries.push({ ...entry, id: newId });
      } else if (keptElementIds.has(entry.id)) {
        // Element survived unchanged
        reconciledEntries.push(entry);
      } else {
        // Element was removed
        removedIds.push(entry.id);
      }
    }

    // Clean up per-element metadata for removed template entries
    for (const removedId of removedIds) {
      delete this._data[removedId];
    }

    if (reconciledEntries.length === 0) {
      // All template elements removed — delete the settings key entirely
      delete this._data[settingsKey];
    } else {
      settings.videoTemplateElements = reconciledEntries;
    }
  }
}
